import styled from '@emotion/styled';
import { Breakpoints } from '@settings';
import classNames from 'classnames';
import { memo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export type ColorSwatchSizes = 'sm' | 'md';

type StyledProps = {
  isActive?: boolean;
  size?: ColorSwatchSizes;
  noHoverEffect?: boolean;
  isAvailable?: boolean;
};

type Props = {
  color: {
    url?: string;
    formattedValue: string;
    isAvailable: boolean;
    isInStock: boolean;
    id: number;
  };
  ignoreDisabledStyle: boolean;
  itemHandler: (swatchData: any) => (e) => void;
  className?: string;
  activeColorValueField?: string;
  fakeHref?: string;
  rel?: string;
} & StyledProps;

const StyledColorSwatch = styled.a<StyledProps>`
  height: ${(props) => `var(--color-swatch-size-${props.size})`};
  width: ${(props) => `var(--color-swatch-size-${props.size})`};
  border: 1px solid #d3d3d3;
  border-radius: var(--color-swatch-border-radius);
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;

  ::before {
    content: '';
    position: absolute;
    display: block;
    top: var(--color-swatch-outline-space);
    bottom: var(--color-swatch-outline-space);
    left: var(--color-swatch-outline-space);
    right: var(--color-swatch-outline-space);
    border-radius: var(--color-swatch-border-radius);
    border: var(--color-swatch-border-width) solid
      ${(props) => (props.isActive ? 'var(--color-swatch-border-color)' : 'transparent')};
    transition: var(--color-swatch-transition);
  }

  :hover {
    ::before {
      border-color: ${(props) => (props.noHoverEffect ? 'transparent' : 'var(--color-swatch-border-color)')};
    }
  }

  @media (max-width: ${Breakpoints.MD}px) {
    height: var(--color-swatch-size-sm);
    width: var(--color-swatch-size-sm);
  }
`;

const StyledColorSwatchDisabled = styled.a<Omit<StyledProps, 'disabled'>>`
  height: ${(props) => `var(--color-swatch-size-${props.size})`};
  width: ${(props) => `var(--color-swatch-size-${props.size})`};
  border: 1px solid #d3d3d3;
  border-radius: var(--color-swatch-border-radius);
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: ${({ isAvailable }) => (isAvailable ? 'pointer' : 'not-allowed')};
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};

  ::before {
    content: '';
    position: absolute;
    display: block;
    top: var(--color-swatch-outline-space);
    bottom: var(--color-swatch-outline-space);
    left: var(--color-swatch-outline-space);
    right: var(--color-swatch-outline-space);
    border-radius: var(--color-swatch-border-radius);
    border: var(--color-swatch-border-width) solid
      ${({ isActive }) => (isActive ? 'var(--swatch-border-color-active)' : '#bbb')};
    transition: var(--color-swatch-transition);
    background: linear-gradient(
      to top left,
      transparent calc(50% - 2px),
      ${({ isActive }) => (isActive ? 'var(--swatch-border-color-active)' : '#bbb')} calc(50% - 1px),
      ${({ isActive }) => (isActive ? 'var(--swatch-border-color-active)' : '#bbb')} calc(50% + 1px),
      transparent calc(50% + 2px)
    );
  }

  :hover {
    ::before {
      border-color: ${(props) => (props.noHoverEffect ? 'transparent' : 'var(--color-swatch-border-color)')};
      background: linear-gradient(
        to top left,
        transparent calc(50% - 2px),
        ${({ isActive }) =>
            isActive ? 'var(--swatch-border-color-active)' : 'var(--swatch-border-color-hover)'}
          calc(50% - 1px),
        ${({ isActive }) =>
            isActive ? 'var(--swatch-border-color-active)' : 'var(--swatch-border-color-hover)'}
          calc(50% + 1px),
        transparent calc(50% + 2px)
      );
    }
  }

  @media (max-width: ${Breakpoints.MD}px) {
    height: var(--color-swatch-size-sm);
    width: var(--color-swatch-size-sm);
  }
`;

const ColorSwatch = ({
  color,
  isActive,
  itemHandler,
  ignoreDisabledStyle,
  size = 'md',
  noHoverEffect = false,
  className = '',
  activeColorValueField = '',
  fakeHref = '#',
  rel,
}: Props) => {
  const isDisabled = !ignoreDisabledStyle && ((!isActive && !color.isAvailable) || !color.isInStock);

  const colorChangeHandler = (e) => {
    e.preventDefault();

    /*if (!isDisabled)*/ itemHandler(color)(e);
  };

  if (isDisabled)
    return (
      <HoverOverlayTrigger noHoverEffect={noHoverEffect} overlay={<Tooltip>{color.formattedValue}</Tooltip>}>
        <StyledColorSwatchDisabled
          className={classNames(`swatch-${color.id}`, className)}
          onClick={colorChangeHandler}
          title={color.formattedValue}
          data-original-title={color.formattedValue}
          data-swatch={color.formattedValue}
          noHoverEffect={noHoverEffect}
          size={size}
          href={fakeHref}
          rel={rel}
          {...(!color.isInStock
            ? {
                isActive,
                isAvailable: color.isAvailable,
              }
            : {})}>
          <span className='visually-hidden'>{color.formattedValue}</span>
        </StyledColorSwatchDisabled>
      </HoverOverlayTrigger>
    );

  return (
    <HoverOverlayTrigger
      noHoverEffect={noHoverEffect}
      overlay={<Tooltip>{color.formattedValue ?? color[activeColorValueField]}</Tooltip>}>
      <StyledColorSwatch
        className={classNames(`swatch-${color.id}`, className)}
        href={fakeHref}
        rel={rel}
        onClick={colorChangeHandler}
        title={color.formattedValue}
        data-original-title={color.formattedValue}
        data-swatch={color.formattedValue}
        size={size}
        noHoverEffect={noHoverEffect}
        isActive={isActive}
        isAvailable={color.isAvailable}>
        <span className='visually-hidden'>{color.formattedValue ?? color[activeColorValueField]}</span>
      </StyledColorSwatch>
    </HoverOverlayTrigger>
  );
};

const HoverOverlayTrigger = ({ noHoverEffect, overlay, children }) => {
  if (noHoverEffect) return children;

  return <OverlayTrigger overlay={overlay}>{children}</OverlayTrigger>;
};

export default memo(ColorSwatch);
